<template>
  <div class="flex flex-col gap-4">
    <!-- Warning Banner (separate from bulk edit section) -->
    <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <AlertTriangleIcon class="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <span class="text-sm text-amber-700">
        {{ $t('settings.availability.schedule.bulk_edit_warning_banner') }}
      </span>
    </div>
    <!-- Header: Title, Description & Edit Button -->
    <div class="flex flex-col gap-0.5">
      <h3 class="text-base font-semibold text-gray-900 mb-1">
        {{ $t('settings.availability.schedule.bulk_edit_weekly_hours_title') }}
      </h3>
      <p class="text-sm text-gray-500 mb-3">
        {{ $t('settings.availability.schedule.bulk_edit_weekly_hours_desc') }}
      </p>
      <button
        id="bulk-edit-weekly-hours-btn"
        class="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
        @click="$emit('open-bulk-edit-modal')"
      >
        <Pencil01Icon class="w-4 h-4" />
        {{ $t('settings.availability.schedule.bulk_edit') }}
      </button>
    </div>

    <!-- Bulk Edit Weekly Hours Section - User List -->
    <div class="flex flex-col gap-2">
      <div
        v-for="user in usersWithSchedules"
        :key="user.id"
        class="rounded-lg border border-gray-200 overflow-hidden"
      >
        <!-- User Row (Clickable Header) -->
        <div
          class="flex items-center justify-between px-3 py-3 cursor-pointer transition-colors bg-white"
          role="button"
          tabindex="0"
          @click="toggleUserExpanded(user.id)"
          @keydown.enter="toggleUserExpanded(user.id)"
          @keydown.space.prevent="toggleUserExpanded(user.id)"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <div
              class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100"
            >
              <UIAvatar
                v-if="user.profilePhoto"
                :src="user.profilePhoto"
                :alt="user.name"
                class="h-10 w-10 text-sm"
              />
              <span v-else class="text-sm font-medium text-gray-600">
                {{ getInitials(user.name) }}
              </span>
            </div>
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-medium text-gray-900 truncate" :title="user.name">{{ user.name }}</span>
              <span class="text-sm text-gray-500 truncate">{{ getAvailabilitySummary(user.weeklySchedule) }}</span>
            </div>
          </div>
          <ChevronRightIcon
            class="h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200"
            :class="{ 'rotate-90': expandedUsers[user.id] }"
          />
        </div>

        <!-- Expanded User Schedule (shown immediately below user row) -->
        <div
          v-show="expandedUsers[user.id]"
          class="p-2"
        >
          <div class="bg-gray-50 px-4 py-2 rounded-md">
            <!-- User's Timezone -->
          <div v-if="user.timezone" class="flex items-center gap-2 py-2 mb-2 border-b border-gray-100">
            <Globe05Icon class="h-4 w-4 text-gray-400" />
            <span class="text-xs text-gray-500">{{ getUserTimezoneLabel(user.timezone) }}</span>
          </div>
          
          <!-- Day-by-day schedule -->
          <div
            v-for="(day, dayIndex) in user.weeklySchedule"
            :key="dayIndex"
            class="flex flex-row items-start py-2"

          >
            <div class="w-20 pt-0.5 text-sm font-medium text-gray-700">
              {{ capitalizeAbbr(day.day || '') }}
            </div>
            <div class="flex flex-col items-start gap-1 flex-1">
              <template v-if="day.isActive && day.intervals.length">
                <span
                  v-for="(slot, i) in day.intervals"
                  :key="i"
                  class="text-sm text-gray-600"
                >
                  {{ formatTime(slot.from) }} - {{ formatTime(slot.to) }}
                </span>
              </template>
              <span v-else class="text-sm text-gray-400">
                {{ $t('settings.availability.schedule.unavailable') }}
              </span>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computedTimezones } from '@/utils/timezonesOptions'
import {
  AlertTriangleIcon,
  ChevronRightIcon,
  Globe05Icon,
  Pencil01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import { UIAvatar } from '@gohighlevel/ghl-ui'
import { computed, PropType, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { DayAvailability } from './DayAvailability'
import { MultiUserSchedule } from './MultiUserSchedule'

const props = defineProps({
  multiUserSchedule: {
    type: Object as PropType<MultiUserSchedule>,
    required: true,
  },
  capitalizeAbbr: {
    type: Function as PropType<(day: string) => string>,
    required: true,
  },
})

defineEmits<{
  (e: 'open-bulk-edit-modal'): void
}>()

const { t } = useI18n()

// Track expanded state for each user
const expandedUsers = reactive<Record<string, boolean>>({})

// Get users with their weekly schedules and timezone
const usersWithSchedules = computed(() => {
  const users = props.multiUserSchedule.getUsers()
  return users.map(user => {
    const schedule = props.multiUserSchedule.getInitialWeeklyScheduleForUser(user.id)
    const userSchedule = props.multiUserSchedule.getScheduleForUser(user.id)
    return {
      ...user,
      weeklySchedule: schedule || [],
      timezone: userSchedule?.timezone || '',
    }
  })
})

function toggleUserExpanded(userId: string) {
  expandedUsers[userId] = !expandedUsers[userId]
}

function getInitials(name: string): string {
  if (!name) return ''
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function formatTime(time: string): string {
  if (!time) return ''
  const [h, m] = time.split(':').map(Number)
  if (isNaN(h) || isNaN(m)) return time
  const date = new Date()
  date.setHours(h)
  date.setMinutes(m)
  return date
    .toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase()
}

function getUserTimezoneLabel(tz: string): string {
  if (!tz) return ''
  const option = computedTimezones.value
    .map(x => x.children)
    .flat()
    .find(x => x.value === tz)
  return option?.label || tz
}

/**
 * Get a summary of the user's availability for display
 * e.g., "Weekdays, 9 am to 5 pm" or "Mon, Tue, Thu, Fri, hours vary"
 */
function getAvailabilitySummary(weeklySchedule: DayAvailability[]): string {
  if (!weeklySchedule || weeklySchedule.length === 0) {
    return t('settings.availability.schedule.no_availability')
  }

  const activeDays = weeklySchedule.filter(d => d.isActive && d.intervals.length > 0)
  
  if (activeDays.length === 0) {
    return t('settings.availability.schedule.no_availability')
  }

  // Check if all active days have the same time slots
  const firstDayIntervals = JSON.stringify(activeDays[0]?.intervals.map(i => ({ from: i.from, to: i.to })))
  const allSameTime = activeDays.every(d => 
    JSON.stringify(d.intervals.map(i => ({ from: i.from, to: i.to }))) === firstDayIntervals
  )

  // Get day names
  const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const activeDayIndices = activeDays.map(d => dayOrder.indexOf(d.day || '')).sort((a, b) => a - b)

  // Check for weekdays (Mon-Fri)
  const isWeekdays = activeDayIndices.length === 5 && 
    activeDayIndices.every((d, i) => d === i + 1) // indices 1-5 are Mon-Fri

  // Check for weekend (Sat-Sun)
  const isWeekend = activeDayIndices.length === 2 && 
    activeDayIndices[0] === 0 && activeDayIndices[1] === 6

  if (allSameTime && activeDays[0]?.intervals[0]) {
    const timeRange = `${formatTime(activeDays[0].intervals[0].from)} to ${formatTime(activeDays[0].intervals[0].to)}`
    
    if (isWeekdays) {
      return `Weekdays, ${timeRange}`
    }
    if (isWeekend) {
      return `Weekend, ${timeRange}`
    }
    
    // Custom days with same time
    const dayAbbrs = activeDays.map(d => props.capitalizeAbbr(d.day || '')).join(', ')
    return `${dayAbbrs}, ${timeRange}`
  }

  // Hours vary
  const dayAbbrs = activeDays.map(d => props.capitalizeAbbr(d.day || '')).join(', ')
  return `${dayAbbrs}, hours vary`
}
</script>
